<!-- Not shown when the grid refuses to open: the refusal is a compact dialog of its own, headed by what
     it says (see `bt-block`), and the blue title bar names a grid that is not there. -->
<div
  class="modal-header bt-header"
  hsDraggable="modal-dialog"
  *ngIf="!vm?.blocked"
>
  <div class="bt-header-left">
    <!-- The white tile marks the dialog as a grid rather than a form — the one place the primary
         colour is inverted out of the header bar. -->
    <span class="bt-header-badge">
      <i class="mdi mdi-table-large"></i>
    </span>
    <div class="bt-heading">
      <h4 class="modal-title bt-title">{{ vm?.title }}</h4>
      <!-- Provenance: which source, which schema, which account. One chip for the whole grid, so it
           says how many accounts there are when the rows do not share one. -->
      <div class="bt-crumbs" *ngIf="vm?.breadcrumb as crumb">
        <span class="bt-crumb">{{ crumb.source }}</span>
        <span class="bt-crumb-sep">·</span>
        <span class="bt-crumb">{{ crumb.tranCategory }}</span>
        <ng-container *ngIf="crumb.account as account">
          <span class="bt-crumb-sep">·</span>
          <span class="bt-crumb-chip">{{ account }}</span>
        </ng-container>
      </div>
    </div>
  </div>
  <span
    class="bt-close"
    role="button"
    tabindex="0"
    title="Close without saving"
    [class.bt-close-disabled]="saving"
    [attr.aria-disabled]="saving || null"
    (click)="dismiss()"
    (keydown.enter)="dismiss()"
  >
    <i class="mdi mdi-close"></i>
  </span>
</div>

<ng-container *ngIf="vm as model">
  <!-- Summary bar: how the selection stands before a single row is read, and the switch that
       decides how much of each row is on screen.

       A sibling of the body rather than its first child, the same rule the single dialog follows: a
       tally you have scrolled past is a tally you cannot act on, and the field-set switch governs
       the whole grid. Keeping it outside the scroll box also leaves the band and header rows free to
       stick to the top of the body with nothing stacked above them. -->
  <div class="bt-summary-bar" *ngIf="!model.blocked" hsDraggable="modal-dialog">
    <span class="bt-tally" *ngFor="let tally of tallies">
      <i class="mdi" [ngClass]="[tally.icon, tally.tone]"></i>
      {{ tally.label }}
    </span>
    <div class="bt-summary-right">
      <span class="bt-tally">
        <i class="mdi mdi-pound bt-tone-quiet"></i>
        {{ rowCountLabel }}
      </span>
      <span class="bt-tally">
        <i class="mdi mdi-table-column bt-tone-quiet"></i>
        {{ fieldCountLabel }}
      </span>
      <!-- A checkbox rather than a two-state switch: the default is every field, and this takes the
           optional ones away. The label is the control, so the whole thing is one target. -->
      <label class="bt-optional-toggle">
        <input
          type="checkbox"
          class="bt-optional-check"
          [checked]="hideOptionalFields"
          (change)="setHideOptionalFields($any($event.target).checked)"
        />
        Hide Optional Fields
      </label>
    </div>
  </div>
</ng-container>

<div
  class="modal-body bt-body"
  [class.nf-loader-parent-element-restrict]="frozen"
  [class.bt-body-locked]="locked"
  [attr.inert]="frozen || locked ? '' : null"
  [attr.aria-busy]="loading || saving || null"
  (scroll)="picker.reanchor()"
>
  <!-- PVAL-26492: reading the selection's trades, or stopped on an error. See the Edit dialog's
       copy — `active` blocks the grid behind it, `errorLabel` freezes it with the message, and the
       restrict class + `inert` stop the body scrolling or taking focus out from under the overlay. -->
  <!-- PVAL-26440: the same overlay locks the grid while a save is in flight. -->
  <hs-loader
    class="{{ frozen ? 'active' : '' }}"
    [loading]="loading || saving"
    [label]="saving ? savingLabel : loadingLabel"
    [errorLabel]="errorLabel"
    [showText]="frozen"
  ></hs-loader>

  <ng-container *ngIf="vm as model">
    <!-- The grid refuses to open, and says what the selection actually held. A bulk edit is one
         schema's fields laid out as columns, so a selection spanning several has no single grid to
         open — and naming the types is what makes the state something to act on rather than a
         failure. -->
    <section class="bt-block" *ngIf="model.block as block">
      <div class="bt-block-head" hsDraggable="modal-dialog">
        <i class="mdi mdi-alert bt-tone-warning"></i>
        <div class="bt-block-copy">
          <h5 class="bt-block-heading">{{ block.heading }}</h5>
          <p class="bt-block-note">{{ block.note }}</p>
        </div>
      </div>
      <div class="bt-block-types" *ngIf="block.types.length">
        <div class="bt-block-type" *ngFor="let type of block.types">
          <span class="bt-block-type-label">{{ type.label }}</span>
          <span class="bt-block-type-count"
            >{{ type.count }} Transaction{{ type.count === 1 ? '' : 's' }}</span
          >
        </div>
      </div>
    </section>

    <!-- The grid. ag-grid gives it row and column virtualisation, range selection and
         keyboard navigation; the design is held by the column defs and the scoped
         overrides in this component's stylesheet. -->
    <div
      class="bt-ag-wrap"
      *ngIf="!model.blocked"
      [style.height.px]="gridHeight"
    >
      <nf-ag-grid
        *ngIf="gridOptions as options"
        gridClass="bt-ag"
        [gridOptions]="options"
        [rowData]="gridRows"
        [gridModules]="gridModules"
        [autoResize]="false"
        (gridReady)="onGridReady($event)"
      ></nf-ag-grid>
    </div>

    <!-- Footer container: a field the metadata places in the footer keeps its container here, and
         takes the grid's own premise with it — ONE value, typed once, written to every transaction
         on save. Per-row copies would just be a column laid on its side.

         It opens empty rather than pre-filled from the first row, which would silently propose
         overwriting every other row's note with that row's. -->
    <section
      class="bt-footer-fields"
      *ngIf="!model.blocked && model.footer.length"
    >
      <div class="bt-footer-field" *ngFor="let field of model.footer">
        <div class="bt-footer-label-row">
          <label class="bt-footer-label" [attr.for]="'bulk-' + field.field">
            {{ field.label }}
            <span class="bt-required" *ngIf="field.required" aria-hidden="true"
              >*</span
            >
          </label>
          <span
            class="bt-footer-clear"
            role="button"
            tabindex="0"
            *ngIf="field.value"
            title="Clear this value"
            (click)="clearFooter(field)"
            (keydown.enter)="clearFooter(field)"
          >
            <i class="mdi mdi-close"></i> Clear
          </span>
        </div>
        <textarea
          #memo
          class="bt-textarea"
          rows="2"
          [id]="'bulk-' + field.field"
          [class.bt-textarea-gap]="requiredGap(field)"
          [attr.placeholder]="field.required ? 'Required' : null"
          [value]="field.value"
          (input)="onFooterEdit(field, memo.value)"
          (blur)="onFooterCommit(field)"
        ></textarea>
        <span
          class="bt-required-hint"
          *ngIf="field.requiredHint as hint"
          [class.bt-required-hint-unmet]="requiredRuleBroken(field)"
          >{{ hint }}</span
        >
      </div>
    </section>
  </ng-container>
</div>

<!-- PVAL-26492: outside the scrolling body, so a failed read is visible without scrolling.
     See the Edit dialog's copy. -->
<hs-dialog-error-details
  [errorHeader]="errorHeader"
  [errorDetails]="errorDetails"
></hs-dialog-error-details>

<div
  class="modal-footer bt-footer"
  [class.bt-footer-blocked]="vm?.blocked"
  hsDraggable="modal-dialog"
>
  <!-- PVAL-26440: a save that went partly through. Those rows created commands, so the grid is
       read-only and the only way forward is to retry what failed, or close. -->
  <div class="bt-save-outcome" role="alert" *ngIf="locked">
    <span class="bt-save-sent">
      <i class="mdi mdi-check-circle"></i>
      {{ sentCount }} transaction{{ sentCount === 1 ? '' : 's' }} sent to the
      commands drawer.
    </span>
    <span class="bt-save-failed" *ngFor="let entry of failedRequests">
      <i class="mdi mdi-alert-octagon"></i>
      Account {{ entry.accountId }} ({{ entry.request.transactions.length }}):
      {{ entry.failure?.message }}
    </span>
  </div>
  <!-- Why the last Save sent nothing — refused before any request, or every request failed. -->
  <span class="bt-save-error" role="alert" *ngIf="!locked && saveError">
    <i class="mdi mdi-alert-octagon"></i>
    {{ saveError }}
  </span>
  <span class="bt-missing" *ngIf="!locked && saveBlocked">
    <i class="mdi mdi-alert-circle"></i>
    {{ missingLabel }}
  </span>
  <!-- Said only once there is nothing left blocking Save, so the two notes never compete for the
       same spot with contradictory advice. -->
  <span class="bt-dirty" *ngIf="!locked && !saveBlocked && changedCount">
    {{ changedCount }} unsaved change{{ changedCount === 1 ? '' : 's' }}
  </span>
  <!-- A range gesture the dialog declined, and why — see RANGE_GESTURE_MAX_ROWS. -->
  <span class="bt-range-note" *ngIf="rangeNotice" role="status">
    <i class="mdi mdi-information-outline"></i>
    {{ rangeNotice }}
  </span>
  <div class="bt-footer-actions">
    <button
      nf-button="cancel"
      *ngIf="!locked && !vm?.blocked"
      [disabled]="saving"
      (click)="dismiss()"
    ></button>
    <!-- The one way out of a refusal: the selection it names is still there in the grid behind. -->
    <button
      nf-button="primary"
      class="bt-back"
      *ngIf="vm?.blocked"
      (click)="dismiss()"
    >
      Back to Selection
    </button>
    <button
      nf-button="secondary"
      class="bt-close-saved"
      *ngIf="locked"
      [disabled]="saving"
      (click)="dismiss()"
    >
      Close
    </button>
    <button
      nf-button="primary"
      class="bt-save"
      *ngIf="vm && !vm.blocked"
      [disabled]="saveDisabled"
      [attr.title]="saveTitle"
      (click)="save()"
    >
      {{ saveLabel }}
    </button>
  </div>
</div>

<!-- The open value panel, rendered ONCE and outside the grid: it is positioned against the viewport,
     not against the row, and only one can be open at a time. It moves itself onto `body`, so the
     dialog's edges cannot clip it. -->
<hs-value-picker
  *ngIf="picker.open"
  [rows]="picker.rows"
  [columns]="picker.columns"
  [style.left.px]="picker.left"
  [style.top.px]="picker.top"
  [style.width.px]="picker.width"
  (picked)="pickValue($event)"
></hs-value-picker>
